import { useState, useMemo } from 'react';
import { Divider, Flex, Stack, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButton } from '~/domains/design-system/Button';

import type { StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';

import type { FlexibleDoublePicksFieldType, FlexibleDoublePicksValue } from './types';
import { FlexibleDoublePicksModal } from './FlexibleDoublePicksModal';
import { filterCompleteSlates } from './utils';

export function FlexibleDoublePicksField({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
}: StaticFieldProps<FlexibleDoublePicksFieldType>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useMantineTheme();
  const { t } = useTranslation('contest');

  const currentValue = useMemo(() => {
    if (!value || typeof value !== 'object') {
      return {
        enabled: field.enabled || false,
        slates: field.defaultSlate
          ? [
              {
                id: 'default',
                slateId: field.defaultSlate,
                percentage: null,
              },
            ]
          : [],
      };
    }
    return value as FlexibleDoublePicksValue;
  }, [value, field.enabled, field.defaultSlate]);

  const handleSaveSlates = (slates) => {
    const completeSlates = filterCompleteSlates(slates);

    onValueChange?.({
      ...currentValue,
      slates: completeSlates,
    });
  };

  return (
    <div>
      <Divider />
      <FieldMeta hideLabel field={field} errors={errors} isTouched={isTouched} />
      <Stack spacing={theme.other.spacing._16}>
        <Flex justify="center" /* eslint-disable-line local-rules/use-theme-tokens */ pt={14} pb={theme.other.spacing._6}>
          <UnstyledButton
            onClick={() => setIsModalOpen(true)}
            data-test-id={dt.action.editSettings}
          >
            <Text variant="label-small" c={theme.colors.blue[9]}>
              {t('manage.common.fields.editSettings')}
            </Text>
          </UnstyledButton>
        </Flex>
      </Stack>

      <FlexibleDoublePicksModal
        isOpen={isModalOpen}
        onToggle={setIsModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveSlates}
        onValueChange={onValueChange}
        currentValue={currentValue}
        value={currentValue.slates}
        slateOptions={field.slateOptions}
        percentageOptions={field.percentageOptions}
        maxSlates={field.maxSlates}
        defaultSlate={field.defaultSlate}
        label={field.label}
        description={field.description}
      />
    </div>
  );
}
